import { Box, Button, Checkbox, Dialog, Flex, Heading, Portal, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { DocumentClass, DocumentCountry, DocumentStatus } from '@ipc/shared'
import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { api, isApiError } from '~/api/client'
import { keys, useDocumentStatus, useFetchRegistration, useRegistration } from '~/api/hooks'
import { ActionBar } from '~/components/member/ActionBar'
import { ErrorAlert, RetryError, useFriendlyError } from '~/components/member/feedback'
import { FlowStepper } from '~/components/member/FlowStepper'
import { FormStack, MemberLayout } from '~/components/member/layout'
import { WhyLink } from '~/components/member/WhyLink'
import { useT } from '~/i18n/use-t'
import { pathForNext, pathForStatus, registrationEndedPath } from '~/lib/flow'
import { useConfigGate, useFlowSteps } from '~/lib/steps'
import { CountryPicker } from './CountryPicker'
import { StepSkeleton } from '~/components/member/StepSkeleton'

const FORM_ID = 'document-form'

/** Moves the browser to the provider (a test replaces it; the page is left for good). */
export const handoff = { go: (url: string) => window.location.assign(url) }

/** The `go` path is ours (same origin, one redirect to the hosted provider); anything else is never followed. */
const isOwnPath = (go: string) => go.startsWith('/') && !go.startsWith('//') && !go.includes('\\')

/** The states in which the member has nothing left to do on this step but go on. */
const DONE_STATES: ReadonlySet<DocumentStatus['state']> = new Set(['exempt', 'evidence_ready', 'not_needed', 'manual'])

/**
 * The step is over for now: no document needed (exempt, or the policy was switched off meanwhile), the document is
 * already checked, or a person will call (the video-call route). One line and one way on: Continue settles the step
 * on the server (face-first spec §3.4 B and D) and goes where the server says (the status page, then the voter page).
 */
export function DocumentDone({
  state,
  onStale,
}: {
  state: 'exempt' | 'evidence_ready' | 'not_needed' | 'manual'
  /** The exemption no longer stood (409 `document_exemption_stale`): the page re-reads the step and says why. */
  onStale?: () => void
}) {
  const { t } = useT()
  const navigate = useNavigate()
  const { providerId } = useParams()
  const fetchRegistration = useFetchRegistration()
  const friendly = useFriendlyError()
  const qc = useQueryClient()
  const next = useMutation({
    mutationFn: async () => {
      // The video-call route is already settled: go where the registration is. The others settle on Continue.
      if (state === 'manual') return pathForStatus(await fetchRegistration())
      const res = await api('registrationDocumentContinue')
      qc.removeQueries({ queryKey: keys.registration })
      return pathForNext(providerId ?? (await fetchRegistration()).provider, res.next)
    },
    onSuccess: (to) => navigate(to),
    onError: (err) => {
      // The server re-evaluated the step (a stale exemption: spec §3.4 B), or the registration is no longer at the
      // document step: re-read both so the page shows what is true now instead of a Continue that keeps failing.
      if (!isApiError(err)) return
      if (err.code === 'document_exemption_stale') {
        onStale?.()
        void qc.invalidateQueries({ queryKey: keys.document })
      } else if (err.code === 'conflict') void qc.invalidateQueries({ queryKey: keys.registration })
    },
  })
  const text = state === 'exempt' || state === 'not_needed' ? 'exempt' : state === 'manual' ? 'cantUseText' : 'ready'
  return (
    <Stack gap={6}>
      <Text>{t(`join.document.${text}`)}</Text>
      {next.isError && <ErrorAlert>{friendly(next.error)}</ErrorAlert>}
      <ActionBar>
        <Button size='lg' w='full' loading={next.isPending} onClick={() => next.mutate()}>
          {t('common.continue')}
        </Button>
      </ActionBar>
    </Stack>
  )
}

/** "I can't use a document": always on the page; a sheet says what happens, then it goes on (spec §10.3, §12.1 step 5). */
function CantUse({ providerId }: { providerId: string }) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const send = useMutation({
    mutationFn: () => api('registrationDocumentCantUse'),
    onSuccess: (res) => {
      qc.removeQueries({ queryKey: keys.registration })
      navigate(pathForNext(providerId, res.next))
    },
  })
  return (
    <>
      <Button variant='outline' w='full' h='auto' minH='12' py={2} whiteSpace='normal' onClick={() => setOpen(true)}>
        {t('join.document.cantUse')}
      </Button>
      <Dialog.Root
        open={open}
        onOpenChange={(e) => {
          if (!e.open && !send.isPending) {
            setOpen(false)
            send.reset()
          }
        }}
        role='alertdialog'
        placement='center'
        scrollBehavior='inside'
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner p={4}>
            <Dialog.Content w='full' maxW='md' maxH='calc(100dvh - 2rem)' my={0}>
              <Dialog.Header>
                <Dialog.Title overflowWrap='anywhere' minW={0}>
                  {t('join.document.cantUse')}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Stack gap={3}>
                  <Text>{t('join.document.cantUseText')}</Text>
                  {send.isError && <ErrorAlert>{friendly(send.error)}</ErrorAlert>}
                </Stack>
              </Dialog.Body>
              <Dialog.Footer flexDirection='column-reverse' alignItems='stretch' gap={2}>
                <Button variant='outline' disabled={send.isPending} onClick={() => setOpen(false)}>
                  {t('join.document.cancel')}
                </Button>
                <Button loading={send.isPending} onClick={() => send.mutate()}>
                  {t('join.document.cantUseConfirm')}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}

/**
 * Country, document type, consent, and the start of the hosted scan (spec §12.1 steps 1-3), on two short screens of
 * one step: first the country and the type; then (`?part=scan&country=..&class=..`) the consent and the start.
 */
function Selector({
  status,
  providerId,
  notice,
  homeCountry,
}: {
  status: DocumentStatus
  providerId: string
  /** Why the member is back at the selector (an exemption that no longer stood). */
  notice?: string | null
  /** The country of the city the member lives in (checked against the IP and the device): the default choice. */
  homeCountry?: string | null
}) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  // The second screen names its choice in the URL; one this step's countries do not offer shows the first screen.
  const wantsScan = params.get('part') === 'scan'
  const urlCountry = wantsScan ? status.countries.find((c) => c.iso2 === params.get('country')) : undefined
  const urlClass = urlCountry?.classes.find((c) => c.class === params.get('class'))
  const atScan = Boolean(urlCountry && urlClass)
  useEffect(() => {
    if (wantsScan && !atScan) navigate(location.pathname, { replace: true })
  }, [wantsScan, atScan, location.pathname, navigate])
  // Undefined until the member picks or clears a country: until then the home country stands (when a provider can
  // check it), also when the registration answers after this step's status. A reload of the second screen keeps its
  // choice for a Back to the first.
  const [picked, setIso2] = useState<string | null | undefined>(() => (urlClass ? urlCountry!.iso2 : undefined))
  const home = homeCountry && status.countries.some((c) => c.iso2 === homeCountry) ? homeCountry : null
  const [klassPicked, setKlass] = useState<DocumentClass | null>(() => urlClass?.class ?? null)
  const iso2 = atScan ? urlCountry!.iso2 : picked === undefined ? home : picked
  const klass = atScan ? urlClass!.class : klassPicked
  const country: DocumentCountry | undefined = status.countries.find((c) => c.iso2 === iso2)
  // The consent is given to what the screen names (spec §10.1): the document, the provider shown for its country and
  // the text version. The tick belongs to that set, so it is gone as soon as any of them changes (also an edited URL).
  const shown = country ? `${country.iso2}|${klass}|${country.provider}|${status.consentVersion}` : null
  const [consentFor, setConsentFor] = useState<string | null>(null)
  const consent = shown !== null && consentFor === shown
  const [consentChanged, setConsentChanged] = useState(false)
  const qc = useQueryClient()
  const chosen = country?.classes.find((c) => c.class === klass)
  const picksReady = Boolean(country && chosen)
  const ready = picksReady && consent

  const start = useMutation({
    mutationFn: async () => {
      const res = await api('registrationDocumentStart', {
        body: {
          country: country!.iso2,
          class: chosen!.class,
          consentDocument: true,
          consentProvider: country!.provider,
          consentVersion: status.consentVersion,
        },
      })
      if (!isOwnPath(res.go)) throw new Error('unsafe_redirect')
      handoff.go(res.go)
    },
    onError: (err) => {
      // The server routes the country to another provider now, or the text changed: show the new text, ask again.
      if (isApiError(err) && err.code === 'document_consent_changed') {
        setConsentFor(null)
        setConsentChanged(true)
        void qc.invalidateQueries({ queryKey: keys.document })
      }
    },
  })
  const consentError = start.isError && isApiError(start.error) && start.error.code === 'document_consent_changed'
  const failure = status.attempt?.failure ?? null
  const hintFor = (c: { class: DocumentClass; sides: 1 | 2 }) =>
    country?.iso2 === 'IL' && c.class === 'id_card' ? 'hintIsrId' : c.sides === 2 ? 'hintTwoSides' : null
  const hint = chosen ? hintFor(chosen) : null
  const hintId = 'document-start-hint'

  const head = (
    <>
      {notice && <ErrorAlert role='status'>{notice}</ErrorAlert>}
      {failure && (
        // R27: the failure is a status, shown in the shared alert style (not bold text).
        <ErrorAlert role='alert'>
          <Stack gap={1}>
            <Text fontWeight='bold'>
              {t(
                failure === 'expired'
                  ? 'join.document.failedExpired'
                  : failure === 'not_accepted'
                    ? 'join.document.failedNotAccepted'
                    : failure === 'our_side'
                      ? 'join.document.failedOurSide'
                      : 'join.document.failed'
              )}
            </Text>
            {status.attempt && <Text>{t('join.document.triesLeft', { n: status.attempt.attemptsLeft })}</Text>}
          </Stack>
        </ErrorAlert>
      )}
    </>
  )

  // No country can be checked right now (the providers or the countries were switched off while the step was
  // open): say so, and leave the one way on that always works.
  if (status.countries.length === 0)
    return (
      <>
        {head}
        <Text>{t('join.document.noCountry')}</Text>
        <CantUse providerId={providerId} />
      </>
    )

  // The first screen: country and type. Continue opens the second screen as its own history entry.
  if (!atScan || !country || !chosen)
    return (
      <>
        {head}
        {/* Keyed per screen: the pressed Continue leaves with its screen, so the new screen's heading takes the focus. */}
        <FormStack
          key='choose'
          id={FORM_ID}
          gap={2}
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            if (!country || !chosen) return
            start.reset()
            navigate(
              `${location.pathname}?${new URLSearchParams({ part: 'scan', country: country.iso2, class: chosen.class })}`
            )
          }}
        >
          <Text>{t('join.document.intro')}</Text>
          <CountryPicker
            countries={status.countries}
            value={iso2}
            onChange={(v) => {
              if (v === iso2) return
              setIso2(v)
              // Another document is another consent: the box on the next screen is empty again.
              setConsentFor(null)
              setConsentChanged(false)
              const next = status.countries.find((c) => c.iso2 === v)
              if (!next?.classes.some((c) => c.class === klass)) setKlass(null)
            }}
          />
          {country && (
            <Box as='fieldset' border='0' p={0} m={0} minW={0}>
              <Text as='legend' fontWeight='medium' mb={1.5}>
                {t('join.document.classLabel')}
              </Text>
              {/* Chips that wrap: two on a row on a 320 px phone, all three from about 400 px. */}
              <Flex wrap='wrap' gap={2}>
                {country.classes.map((c) => (
                  <Stack
                    as='label'
                    key={c.class}
                    direction='row'
                    align='center'
                    flex='1 1 auto'
                    gap={3}
                    minH='12'
                    px={3}
                    py={2}
                    borderWidth='1px'
                    borderColor={klass === c.class ? 'fg' : 'border.input'}
                    borderRadius='md'
                    bg='bg'
                    cursor='pointer'
                  >
                    <input
                      type='radio'
                      name='documentClass'
                      value={c.class}
                      checked={klass === c.class}
                      onChange={() => {
                        setKlass(c.class)
                        setConsentFor(null)
                        setConsentChanged(false)
                      }}
                      style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0, accentColor: 'currentColor' }}
                    />
                    <span>{t(`join.document.class.${c.class}`)}</span>
                  </Stack>
                ))}
              </Flex>
            </Box>
          )}
          <ActionBar gap={2}>
            {!picksReady && (
              <Text id={hintId} color='fg.muted'>
                {t('join.document.chooseHint')}
              </Text>
            )}
            <Button
              type='submit'
              form={FORM_ID}
              size='lg'
              w='full'
              disabled={!picksReady}
              aria-describedby={picksReady ? undefined : hintId}
            >
              {t('common.continue')}
            </Button>
          </ActionBar>
        </FormStack>
        <CantUse providerId={providerId} />
      </>
    )

  // The second screen: what is scanned, the consent to who checks it, and the start of the scan.
  return (
    <>
      <FormStack
        key='scan'
        id={FORM_ID}
        gap={2}
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          if (ready && !start.isPending) start.mutate()
        }}
      >
        <Text fontWeight='bold'>
          {t(`join.document.class.${chosen.class}`)} · {country.label}
        </Text>
        {/* How to photograph it: the last thing read before the scan opens. */}
        {hint && <Text role='note'>{t(`join.document.${hint}`)}</Text>}
        <Text color='fg.muted'>
          {t('join.document.why')} <WhyLink faq='document' inline />
        </Text>
        <Checkbox.Root
          checked={consent}
          onCheckedChange={(e) => {
            setConsentFor(e.checked === true ? shown : null)
            if (e.checked === true) setConsentChanged(false)
          }}
          alignItems='flex-start'
          name='consentDocument'
          gap={3}
          // As the face consent: the choice-card frame from `md`; on a phone the box itself shows the state.
          borderWidth={{ base: 0, md: '1px' }}
          borderColor={consent ? 'fg' : 'border'}
          borderRadius='md'
          p={{ base: 0, md: 4 }}
          cursor='pointer'
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control boxSize='5' mt='0.5' />
          <Checkbox.Label fontSize='md' lineHeight='1.5'>
            {t('join.document.consent', { provider: country.providerName })}
          </Checkbox.Label>
        </Checkbox.Root>
        {consentChanged && !consent && <ErrorAlert>{t('join.document.consentChanged')}</ErrorAlert>}
        {start.isError && !consentError && <ErrorAlert>{friendly(start.error)}</ErrorAlert>}
        <ActionBar gap={2}>
          {!ready && (
            <Text id={hintId} color='fg.muted'>
              {t('join.document.continueHint')}
            </Text>
          )}
          <Button
            type='submit'
            form={FORM_ID}
            size='lg'
            w='full'
            // One line on a 320 px phone (the label is long).
            px={3}
            disabled={!ready}
            // The page is left once the redirect starts: keep the button busy until then.
            loading={start.isPending || start.isSuccess}
            aria-describedby={ready ? undefined : hintId}
          >
            {t('join.document.continue')}
          </Button>
        </ActionBar>
      </FormStack>
      <CantUse providerId={providerId} />
    </>
  )
}

/** `/join/:providerId/document` (S4b: the step after the face check, while the requirement applies). */
export default function DocumentStep() {
  const { t } = useT()
  const { providerId = '' } = useParams()
  const { config, failed, pending } = useConfigGate()
  const { required } = useFlowSteps()
  // Always read: a registration at `document_pending` shows this page even when the policy was switched off meanwhile.
  const status = useDocumentStatus()
  const reg = useRegistration()
  // Set when an exempt Continue answered stale: the selector that follows says why the member is back there.
  const [stale, setStale] = useState(false)
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  // The policy decides whether the step exists: wait for it before sending anyone away.
  if (pending) return null
  // A failed config read is not "the step is off": show it with a retry instead of sending the member away.
  if (failed)
    return (
      <MemberLayout flow>
        <RetryError error={config.error} onRetry={() => void config.refetch()} />
      </MemberLayout>
    )

  const s = status.data
  let body
  // Back on the selector's second screen goes to its first one.
  let backTo: string | undefined
  if (status.isPending) body = <StepSkeleton />
  else if (status.isError) {
    if (isApiError(status.error) && status.error.status === 401)
      return <Navigate {...registrationEndedPath(providerId)} replace />
    body = <RetryError error={status.error} onRetry={() => void status.refetch()} />
  } else if (s) {
    // `not_needed` is the case-D Continue (policy switched off while the step was open) only at `document_pending`;
    // a member already settled (decided) who lands here is sent where the registration is, as on `!applies`.
    if (s.state === 'not_needed' && reg.data?.stage === 'document_pending') body = <DocumentDone state='not_needed' />
    else if (!s.applies || s.state === 'not_needed') {
      // Nothing to do here (the requirement does not apply to this registration): go where the registration is.
      if (reg.data) return <Navigate to={pathForStatus(reg.data)} replace />
      if (reg.isError) {
        if (isApiError(reg.error) && reg.error.status === 401)
          return <Navigate {...registrationEndedPath(providerId)} replace />
        body = <RetryError error={reg.error} onRetry={() => void reg.refetch()} />
      } else body = <StepSkeleton />
    } else if (s.state === 'checking' || s.state === 'confirm_name')
      return <Navigate to='/join/document/return' replace />
    else if (DONE_STATES.has(s.state))
      body = (
        <DocumentDone
          state={s.state as 'exempt' | 'evidence_ready' | 'not_needed' | 'manual'}
          onStale={() => setStale(true)}
        />
      )
    else {
      // Only while the second screen is really shown (a valid choice in the URL): not on a leftover entry.
      const scanCountry =
        params.get('part') === 'scan' ? s.countries.find((c) => c.iso2 === params.get('country')) : null
      if (scanCountry?.classes.some((c) => c.class === params.get('class'))) backTo = pathname
      body = (
        <Selector
          status={s}
          providerId={providerId}
          notice={stale ? t('errors.document_exemption_stale') : null}
          homeCountry={reg.data?.city?.country}
        />
      )
    }
  }
  return (
    // No Back row on the first screen: the face check is done and cannot be redone before the document is settled.
    <MemberLayout flow backTo={backTo} backHistory={backTo !== undefined}>
      {required ? (
        <FlowStepper step='document' />
      ) : (
        <Heading as='h1' fontSize='2xl' lineHeight='1.25'>
          {t('steps.document')}
        </Heading>
      )}
      {body}
    </MemberLayout>
  )
}
